import type {ReactElement, RefObject} from 'react';
import React, {isValidElement} from 'react';
import type {ButtonProps} from '@Components/button';
import {Button, IconPlacement} from '@Components/button';
import {Text, TextSize, TextType} from '@Components/text';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {AIButtonShape, AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import styles from './ai-button.module.scss';

export interface AiButtonProps extends ButtonProps {
  buttonRef?: RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
  variant?: AIButtonVariant;
  shape?: AIButtonShape;
  topPillContent?: string | ReactElement<any>;
  pillToolTipContent?: string | ReactElement<any>;
  pillClassName?: string;
  isLoadingButtonOverlayShown?: boolean;
  isAiIconHidden?: boolean;
  isPulsating?: boolean;
  className?: string;
  textWithLoadingState?: string;
}

export function AiButton({variant = AIButtonVariant.DEFAULT, shape = AIButtonShape.SQUARE, ...props}: AiButtonProps): ReactElement<any> {
  const isActive = variant === AIButtonVariant.DEFAULT || props.isActive;
  const isPremium = variant === AIButtonVariant.PREMIUM || variant === AIButtonVariant.PREMIUM_SUBDUED || variant === AIButtonVariant.PREMIUM_2;
  const isPremium2 = variant === AIButtonVariant.PREMIUM_2;
  const isSubdued = variant === AIButtonVariant.PREMIUM_SUBDUED || variant === AIButtonVariant.SUBDUED;

  const getButtonClassNames = (): string => {
    return `
      ${styles.aiButton} 
      ${props.customClasses ?? ''} 
      ${isActive || (!isSubdued && !isPremium2) ? styles.active : ''} 
      ${isPremium ? styles.premium : ''}
      ${isPremium2 ? styles.premium2 : ''} 
      ${props.disabled ? styles.disabled : ''}
      ${shape === AIButtonShape.SQUARE ? styles.squared : ''}
    `;
  };

  const getPillClassNames = (): string => {
    return `
      radius-round 
      flex-center 
      ${styles.pill} 
      ${props.isLoading || props.disabled ? styles.disabled : ''} 
      ${isPremium ? styles.premium : ''} 
      ${props.pillClassName ?? ''}
    `;
  };

  const getLoadingButtonOverlay = (): ReactElement<any> | null => {
    if (!props.isLoading || props.disabled) {
      return null;
    }

    if (!props.isLoadingButtonOverlayShown) {
      return null;
    }

    return <div className={`${styles.loadingButton} radius-4`} />;
  };

  const renderAiButton = (): ReactElement<any> => {
    if (props.topPillContent || (isPremium && !isPremium2)) {
      return getButtonWithPill();
    }

    return getButtonWithoutPill();
  };

  const getPulses = (): ReactElement<any> | null => {
    if (!props.isPulsating) {
      return null;
    }

    return (
      <>
        <div className={`${styles.pulse} ${styles.pulse1}`} />
        <div className={`${styles.pulse} ${styles.pulse2}`} />
        <div className={`${styles.pulse} ${styles.pulse3}`} />
      </>
    );
  };

  const shouldShowGradientLoader = (): boolean => {
    if (!isSubdued) {
      return false;
    }
    return props.isLoading as boolean;
  };

  const getLoadingText = (): ReactElement<any> | null => {
    if (!props.textWithLoadingState) {
      return null;
    }

    return <Text val={props.textWithLoadingState} size={TextSize.XSMALL} bold className={styles.loadertext} />;
  };

  const getGradientLoaderWithLoadingStateTextForSubduedButton = (): ReactElement<any> => {
    return (
      <div className={styles.gradientLoaderWithTextContainer}>
        <div className={`flex-center ${styles.gradientLoaderWithText}`}>
          <progress className="icon-circular-loader -xsmall -aidark" />
        </div>
        <span className="spacing-m-l-2 content-ai">{props.loadingStateText}</span>
      </div>
    );
  };

  const getGradientLoaderForSubduedButton = (): ReactElement<any> | null => {
    if (!shouldShowGradientLoader()) {
      return null;
    }

    if (props.loadingStateText) {
      return getGradientLoaderWithLoadingStateTextForSubduedButton();
    }

    return (
      <div className={`${styles.gradientLoader}  ${props.textWithLoadingState ? `${styles.loaderContainerWithText} spacing-p-b-1` : ''}`}>
        <progress className="icon-circular-loader -xsmall -aidark" />
        {getLoadingText()}
      </div>
    );
  };

  const getButtonWithoutPill = (): ReactElement<any> => {
    return (
      <div className={`${styles.pulseContainer} ${props.isFullWidth ? `_full-width ${styles.fullWidth}` : '_fit-width'} ${props.className ?? ''}`}>
        <Button
          {...props}
          ref={props.buttonRef}
          icon={props.isAiIconHidden ? undefined : (props.icon ?? 'icon-ai')}
          iconPlacement={props.iconPlacement ?? IconPlacement.LEFT}
          customClasses={getButtonClassNames()}
          textClasses={`${styles.aiButtonText} ${props.textClasses ?? ''}`}
          iconClassName={`${styles.aiButtonIcon} ${props.iconClassName ?? ''}`}
          loaderClassName={`${styles.aiButtonLoader} ${isSubdued ? '_hidden align-self-center' : ''} ${props.loaderClassName ?? ''}`}
          text={props.isLoading && props.textWithLoadingState ? props.textWithLoadingState : props.text}
        >
          <span className={styles.hoverElementGradient} />
          <span className={styles.clickElementGradient} />
          {getGradientLoaderForSubduedButton()}
          {getLoadingButtonOverlay()}
          {getPulses()}
        </Button>
      </div>
    );
  };

  const getOverlayForTrigger = (): ReactElement<any> => {
    return (
      <Tooltip id="ai-cta-tooltip" className="spacing-m-b-3 _pointer-events-none">
        {props.pillToolTipContent}
      </Tooltip>
    );
  };

  const getTopPillContent = (): ReactElement<any> | null => {
    if (isPremium) {
      return <Icon icon="icon-crown" type={IconType.PREMIUM} shape={IconShape.NONE} size={IconSize.SIZE_ICON_20} className={styles.premiumTagIcon} />;
    }

    if (isValidElement(props.topPillContent)) {
      return props.topPillContent;
    }

    if (typeof props.topPillContent === 'string') {
      return <Text className="content-body-white" type={TextType.BODY} size={TextSize.XXSMALL} bold val={props.topPillContent} />;
    }

    throw new Error('Invalid props.topPillContent');
  };

  const getPillContainer = (): ReactElement<any> => {
    if (!props.pillToolTipContent) {
      return <div className={getPillClassNames()}>{getTopPillContent()}</div>;
    }

    return (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" delay={{show: 50, hide: 100}} overlay={getOverlayForTrigger()} key="ai-cta-overlay" offset={[0, -5]}>
        <div className={getPillClassNames()}>{getTopPillContent()}</div>
      </OverlayTrigger>
    );
  };

  const getButtonWithPill = (): ReactElement<any> => {
    return (
      <div className={`${styles.buttonContainer} ${props.customClasses} ${props.isFullWidth ? '_full-width' : '_fit-width'}`}>
        {getPillContainer()}
        {getButtonWithoutPill()}
      </div>
    );
  };

  return renderAiButton();
}
